import { ApiV1_Subsidiary } from '@pedix-workspace/api-v1';
import {
  getDateReferenceForTimezone,
  getDistanceFromLatLonInKm,
  getEstablishmentDateReference,
  getEstablishmentTimezone,
  isEstablishmentOpened,
  isEstablishmentTemporaryClosed,
  LatLng,
} from '@pedix-workspace/utils';

export type SubsidiariesByGroup = {
  key: string;
  groupName: string;
  subsidiaries: ApiV1_Subsidiary[];
};
export type SubsidiariesByGroupAndDistance = SubsidiariesByGroup & {
  distance: number;
};

export function getSubsidiaryGroupName(
  subsidiary: Pick<ApiV1_Subsidiary, 'subsidiaryGroup' | 'addressDetails'>,
) {
  return subsidiary.subsidiaryGroup || subsidiary.addressDetails?.state || 'Otras sucursales';
}

export function getSortedSubsidiariesByGroup(subsidiaries: ApiV1_Subsidiary[]) {
  const subsidiariesByGroup = subsidiaries.reduce(
    (acc, subsidiary) => {
      const groupName = getSubsidiaryGroupName(subsidiary);

      if (!acc[groupName]) {
        acc[groupName] = {
          key: groupName,
          groupName,
          subsidiaries: [],
        };
      }
      acc[groupName].subsidiaries.push(subsidiary);

      return acc;
    },
    {} as Record<string, SubsidiariesByGroup>,
  );
  return Object.values(subsidiariesByGroup);
}

export function getSortedSubsidiariesByGroupAndDistance(
  subsidiaries: ApiV1_Subsidiary[],
  userLocation: LatLng,
  selectedSubsidiaryId?: string,
) {
  if (!userLocation || !subsidiaries.length) {
    return [];
  }

  const subsidiariesByGroupMap = subsidiaries.reduce(
    (acc, subsidiary) => {
      const groupName = getSubsidiaryGroupName(subsidiary);

      if (!acc[groupName]) {
        acc[groupName] = {
          key: groupName,
          groupName,
          subsidiaries: [],
          distance: 0,
        };
      }
      acc[groupName].subsidiaries.push(subsidiary);

      return acc;
    },
    {} as Record<string, SubsidiariesByGroupAndDistance>,
  );
  return Object.values(subsidiariesByGroupMap)
    .map(subsidiaryByGroup => ({
      ...subsidiaryByGroup,
      distance: getMinDistanceToSubsidiariesByGroup(userLocation, subsidiaryByGroup),
    }))
    .sort((a, b) => {
      if (a.subsidiaries.find(subsidiary => subsidiary.id === selectedSubsidiaryId)) {
        return -1;
      }
      if (b.subsidiaries.find(subsidiary => subsidiary.id === selectedSubsidiaryId)) {
        return 1;
      }
      return a.distance - b.distance;
    });
}

export function isSubsidiaryTemporaryClosed(
  subsidiary: Pick<ApiV1_Subsidiary, 'temporaryClosure' | 'addressDetails' | 'countryCode'>,
): boolean {
  return isEstablishmentTemporaryClosed(subsidiary.temporaryClosure, {
    dateReference: getEstablishmentDateReference(subsidiary),
  });
}

export function getSubsidiariesWithoutTemporaryClosure(
  subsidiaries: ApiV1_Subsidiary[],
): ApiV1_Subsidiary[] {
  return subsidiaries.filter(subsidiary => !isSubsidiaryTemporaryClosed(subsidiary));
}

export function getFirstOpenedSubsidiaryOrDefault(
  subsidiaries: ApiV1_Subsidiary[],
): ApiV1_Subsidiary | undefined {
  const selectableSubsidiaries = getSubsidiariesWithoutTemporaryClosure(subsidiaries);
  const openSubsidiary = selectableSubsidiaries.find(subsidiary => {
    const timezone = getEstablishmentTimezone(subsidiary);
    const dateReference = timezone ? getDateReferenceForTimezone(timezone) : undefined;

    return isEstablishmentOpened(subsidiary.workingDays, dateReference);
  });
  return openSubsidiary || selectableSubsidiaries[0] || subsidiaries[0];
}

function getMinDistanceToSubsidiariesByGroup(
  userLocation: LatLng,
  subsidiariesByGroup: SubsidiariesByGroup,
): number {
  return subsidiariesByGroup.subsidiaries.reduce((minDistance, subsidiary) => {
    const coordinates = subsidiary.addressDetails?.coordinates;

    if (!coordinates) {
      return minDistance;
    }
    const distance = getDistanceFromLatLonInKm(userLocation, coordinates);

    return Math.min(minDistance, distance);
  }, Number.MAX_SAFE_INTEGER);
}
